import Badge from "@/components/base/Badge";
import { formatMoney } from "@/utils/format";
import type { School, PlatformStat } from "@/types";

interface FinanceComparisonProps {
  schools: School[];
  stats: PlatformStat[];
}

interface Row {
  school: School;
  stat: PlatformStat;
}

export default function FinanceComparison({ schools, stats }: FinanceComparisonProps) {
  const rows: Row[] = schools
    .map((school) => ({ school, stat: stats.find((s) => s.schoolId === school.id) }))
    .filter((r): r is Row => Boolean(r.stat));

  const totalCollected = rows.reduce((sum, r) => sum + r.stat.collectedFees, 0);
  const totalExpected = rows.reduce((sum, r) => sum + r.stat.expectedFees, 0);
  const totalExpenses = rows.reduce((sum, r) => sum + r.stat.expenses, 0);
  const totalRevenue = rows.reduce((sum, r) => sum + r.stat.revenue, 0);
  const totalNet = totalRevenue - totalExpenses;
  const globalRate = totalExpected > 0 ? Math.round((totalCollected / totalExpected) * 100) : 0;

  const maxAmount = Math.max(
    1,
    ...rows.map((r) => Math.max(r.stat.expectedFees, r.stat.collectedFees, r.stat.expenses)),
  );

  const kpis = [
    { label: "Recettes totales", value: formatMoney(totalRevenue), icon: "ri-wallet-3-line", tone: "accent" },
    { label: "Dépenses totales", value: formatMoney(totalExpenses), icon: "ri-shopping-bag-3-line", tone: "secondary" },
    { label: "Résultat net", value: formatMoney(totalNet), icon: "ri-line-chart-line", tone: "primary" },
    { label: "Taux de recouvrement", value: `${globalRate}%`, icon: "ri-percent-line", tone: "primary" },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {kpis.map((kpi) => (
          <div key={kpi.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
            <span
              className={`w-9 h-9 rounded-md flex items-center justify-center ${
                kpi.tone === "accent"
                  ? "bg-accent-50 text-accent-700"
                  : kpi.tone === "secondary"
                    ? "bg-secondary-100 text-secondary-700"
                    : "bg-primary-50 text-primary-700"
              }`}
            >
              <i className={`${kpi.icon} text-lg`}></i>
            </span>
            <p className="mt-3 text-xs font-medium uppercase tracking-wide text-foreground-500">{kpi.label}</p>
            <p className="mt-1 text-lg font-semibold font-heading text-foreground-950 whitespace-nowrap">
              {kpi.value}
            </p>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        {rows.map(({ school, stat }) => {
          const net = stat.revenue - stat.expenses;
          const rate = stat.expectedFees > 0
            ? Math.round((stat.collectedFees / stat.expectedFees) * 100)
            : 0;
          const collectedWidth = Math.max(3, Math.round((stat.collectedFees / maxAmount) * 100));
          const expenseWidth = Math.max(3, Math.round((stat.expenses / maxAmount) * 100));
          return (
            <div
              key={school.id}
              className="rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-background-300"
            >
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 shrink-0 overflow-hidden rounded-md border border-background-200 bg-background-100 flex items-center justify-center">
                  {school.logo ? (
                    <img
                      src={school.logo}
                      alt={school.name}
                      title={school.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <i className="ri-building-2-line text-foreground-500"></i>
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold font-heading text-foreground-950">{school.shortName}</p>
                  <p className="truncate text-xs text-foreground-500">
                    Attendu : {formatMoney(stat.expectedFees)}
                  </p>
                </div>
                <div className="text-right">
                  <p
                    className={`text-sm font-semibold font-heading whitespace-nowrap ${
                      net >= 0 ? "text-primary-700" : "text-accent-700"
                    }`}
                  >
                    {net >= 0 ? "+" : ""}
                    {formatMoney(net)}
                  </p>
                  <p className="text-xs uppercase tracking-wide text-foreground-400">résultat net</p>
                </div>
                <Badge tone={rate >= 90 ? "success" : rate >= 75 ? "secondary" : "danger"}>{rate}%</Badge>
              </div>

              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-3">
                  <span className="w-24 shrink-0 text-xs text-foreground-500">Encaissé</span>
                  <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-background-100">
                    <span
                      className="block h-full rounded-full bg-accent-500"
                      style={{ width: `${collectedWidth}%` }}
                    ></span>
                  </span>
                  <span className="w-32 shrink-0 text-right text-xs font-medium text-foreground-900 whitespace-nowrap">
                    {formatMoney(stat.collectedFees)}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-24 shrink-0 text-xs text-foreground-500">Dépenses</span>
                  <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-background-100">
                    <span
                      className="block h-full rounded-full bg-secondary-500"
                      style={{ width: `${expenseWidth}%` }}
                    ></span>
                  </span>
                  <span className="w-32 shrink-0 text-right text-xs font-medium text-foreground-900 whitespace-nowrap">
                    {formatMoney(stat.expenses)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-accent-500"></span> Recettes encaissées
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-secondary-500"></span> Dépenses
        </span>
        <span>Montants dans la devise de chaque école.</span>
      </div>
    </div>
  );
}